/**
 * Translation for TENANT-AUTHORED form content.
 *
 * The app chrome has been translatable for a long time (react-i18next plus
 * `src/i18n/locales/es.json`, and a `translation_keys` override layer with an
 * admin UI). What was never translatable is the content an org writes itself:
 * `com_fields.friendly_name`, `field_options[].label`, form section titles, help
 * text. FormRenderer and PublicFormPage rendered those raw — `{field.label}`,
 * `{opt.label}` — so toggling to Spanish gave you Spanish buttons around an
 * English form. On VYC's public referral form, the only self-referral entry
 * point they have, that means a Spanish-speaking member cannot self-refer.
 *
 * The backend registers those strings as `translation_keys` with
 * `is_dynamic = true` and serves them as one flat bundle
 * (`GET /api/public/forms/{token}/translations/{lang}` for an anonymous
 * visitor, `GET /api/translations/dynamic/{lang}` for a signed-in user). This
 * module is the rendering side: a caller fetches the bundle and hands it in.
 *
 * Safe to ship before a single Spanish string exists. The backend serves
 * untranslated keys as their English source text, and an absent key falls back
 * to the tenant's own label — so a form with no translations renders exactly as
 * it does today, never blank and never a mangled key.
 *
 * Key derivation and the identity translator live in `dynamicTranslationKeys.ts`
 * — importing this module needs a DOM, that one does not.
 */

import { useCallback, useContext, useMemo } from 'react';
import TranslationContext from '@/contexts/TranslationContext';
import {
  fieldKeyCandidates,
  indexDynamicBundle,
  optionKeyCandidates,
  publicFormKeyCandidates,
  sectionKeyCandidates,
  type DynamicTranslator,
} from './dynamicTranslationKeys';

export {
  slugify,
  fieldKeyOf,
  identityTranslator,
  indexDynamicBundle,
  MAX_SLUG_COMPONENT,
} from './dynamicTranslationKeys';
export type { DynamicTranslator, FieldRef } from './dynamicTranslationKeys';

/**
 * Translator for tenant-authored form content.
 *
 * @param bundle Flat `{key_name: text}` map from the dynamic-translations
 *   endpoint. Omit it on surfaces that have not fetched one — everything then
 *   falls back to the tenant's own text, which is the current behavior.
 *
 * Degrades to the tenant's text when there is no TranslationProvider above it,
 * so a preview surface or a bare test still renders.
 */
export function useDynamicTranslation(bundle?: Record<string, string> | null): DynamicTranslator {
  const ctx = useContext(TranslationContext);
  const t = ctx?.t;
  const language = ctx?.language || 'en';

  // Indexed once per bundle, not once per label: a referral form has ~40 fields
  // and several hundred option labels, and re-deriving aliases on every render
  // would be the most expensive thing on the page.
  const index = useMemo(() => indexDynamicBundle(bundle), [bundle]);

  const resolve = useCallback(
    (candidates: string[], raw?: string | null): string => {
      const fallback = raw ?? '';
      // Nothing authored, nothing to translate. Critically this must NOT reach
      // t(): given an empty fallback, t() falls through to its last resort,
      // which humanizes the KEY — so an absent placeholder would render as the
      // literal word "Placeholder".
      if (!fallback) return '';
      for (const candidate of candidates) {
        const hit = index[candidate];
        if (hit) return hit;
      }
      return fallback;
    },
    [index],
  );

  return {
    language,
    label: (field, raw) => resolve(fieldKeyCandidates(field, 'label'), raw),
    // 'help_text' is the attribute name the backend registers under.
    help: (field, raw) => resolve(fieldKeyCandidates(field, 'help_text'), raw),
    description: (field, raw) => resolve(fieldKeyCandidates(field, 'description'), raw),
    placeholder: (field, raw) => resolve(fieldKeyCandidates(field, 'placeholder'), raw),
    option: (field, optionValue, raw) => resolve(optionKeyCandidates(field, optionValue), raw ?? optionValue),
    sectionTitle: (sectionId, raw) => resolve(sectionKeyCandidates(sectionId, 'title'), raw),
    sectionDescription: (sectionId, raw) => resolve(sectionKeyCandidates(sectionId, 'description'), raw),
    publicForm: (attr, raw) => resolve(publicFormKeyCandidates(attr), raw),
    chrome: (key, fallback) => (t ? t(key, fallback) : fallback),
  };
}
